/**
 * How trees stand on a farm, in the words a forester or an auditor uses (owner, 2026-09-27: "production
 * type (ex. Agriculture etc.) plantation type (boundary, mixed, diagonal, full etc. as relevant -
 * with good terminologies?)"). The ids are the ones migration 20260927470000 allows.
 */
export const PRODUCTION_SYSTEMS = [
  { id: 'agroforestry', label: 'Agroforestry (trees with crops)' },
  { id: 'farm_forestry', label: 'Farm forestry (woodlot)' },
  { id: 'plantation_forestry', label: 'Plantation forestry' },
  { id: 'agriculture', label: 'Agriculture (trees on cropland)' },
  { id: 'horticulture', label: 'Horticulture (orchard)' },
  { id: 'silvopasture', label: 'Silvopasture (trees with grazing)' },
  { id: 'natural_regeneration', label: 'Natural / assisted regeneration' },
];

/** Arrangement, and for a block its layout: square, rectangular, quincunx (diagonal) or triangular. */
export const PLANTING_PATTERNS = [
  { id: 'block_square', label: 'Block - square layout (full cover)' },
  { id: 'block_rectangular', label: 'Block - rectangular layout' },
  { id: 'block_quincunx', label: 'Block - quincunx (diagonal) layout' },
  { id: 'block_triangular', label: 'Block - triangular (hexagonal) layout' },
  { id: 'boundary', label: 'Boundary / bund planting' },
  { id: 'line', label: 'Line / avenue (single row)' },
  { id: 'alley', label: 'Alley cropping (rows between crops)' },
  { id: 'scattered', label: 'Scattered trees on farmland' },
  { id: 'mixed', label: 'Mixed-species stand' },
  { id: 'live_fence', label: 'Live fence / windbreak / shelterbelt' },
  { id: 'homestead', label: 'Homestead / home garden' },
];

/** What a fruit or NTFP species bears each year. */
export const PRODUCE_TYPES = [
  { id: 'fruit', label: 'Fruit' }, { id: 'nut', label: 'Nuts' }, { id: 'seed', label: 'Seeds' },
  { id: 'pod', label: 'Pods' }, { id: 'leaf', label: 'Leaves' }, { id: 'flower', label: 'Flowers' },
  { id: 'latex', label: 'Latex' }, { id: 'resin_gum', label: 'Resin / gum' }, { id: 'bark', label: 'Bark' },
  { id: 'fodder', label: 'Fodder' }, { id: 'fuelwood', label: 'Fuelwood (lopping)' }, { id: 'other', label: 'Other' },
];

/** Form factor for a standing tree's stem volume over bark: the usual default where none is measured. */
export const FORM_FACTOR = 0.5;
const CUBIC_FEET_PER_CUBIC_METRE = 35.3147;

/**
 * Standing volume of `trees` trees of an average girth (cm, at breast height) and height (m):
 * basal area (girth² / 4π) x height x form factor. In the unit asked for when it is a volume
 * (CBM or CFT); null for a weight, which needs a wood density nobody has given.
 */
export function standingVolume(girthCm: number | null, heightM: number | null, trees: number | null, unitCode: string | undefined): number | null {
  if (girthCm === null || heightM === null || trees === null || girthCm <= 0 || heightM <= 0 || trees <= 0) return null;
  const girthM = girthCm / 100;
  const cubicMetres = ((girthM * girthM) / (4 * Math.PI)) * heightM * FORM_FACTOR * trees;
  if (unitCode === 'CBM') return cubicMetres;
  if (unitCode === 'CFT') return cubicMetres * CUBIC_FEET_PER_CUBIC_METRE;
  return null;
}
